/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Maybe } from "~/tools";

export enum Period {
  MONTH = 1,
  QUARTER = 3,
  YEAR = 12,
  RANGE = 13,
}

export enum ChartType {
  ACCUMULATED_COST = "Accumulated Cost",
  ANOMALY_COST = "Anomaly Cost",
  COST_PER_ENVIRONMENT = "Cost per Environment",
  COST_PER_CATEGORY = "Cost per Category",
  COST_VS_BUDGET = "Cost vs Budget",
  COST_PER_APPLICATION = "Cost per Application",
  COST_PER_SERVICES = "Cost per Service",
}

export enum Granularity {
  DAYS = "days",
  MONTHS = "months",
}

export enum CostAnalysisFields {
  TOTAL_COST = "Total cost",
  OTHER_APPLICATIONS_COST = "All other applications",
  OTHER_CATEGORIES_COST = "All other categories",
  CORRECTION_COST = "Google Cloud invoice month correction",
  DAILY_COST = "Daily cost",
  MONTHLY_COST = "Monthly cost",
  COST_BEFORE_CORRECTION = "Daily cost before correction",
  OTHER_SERVICES_COST = "All other services",
  ANOMALY_COST = "cost",
  SAVINGS_PLANS = "Savings Plans commitments",
}

// refers to a size of group
export const topSpendingGroup = {
  ENVIRONMENTS: 5,
  APPLICATIONS: 5,
  CATEGORIES: 4,
} as const;

export const chartTypeOptions = Object.values(ChartType).map((chartType) => ({
  value: chartType,
  label: chartType,
}));

export const PlaceholderCategory = {
  UNCATEGORIZED: {
    id: "Uncategorized",
    name: "Uncategorized",
  },
  OTHER: {
    id: "Other",
    name: "Other categories",
  },
} as const;

export type ChartOption = {
  value: ChartType;
  label: ChartType;
};

export type TimePoint = {
  date: string;
  value: number;
};

export type TableData = {
  [key: string]: string[] | number | string | undefined;
  // everything that is not a date-specific cost value is explicitly defined below
  id?: string;
  field: string;
  total: number | string;
  types?: string[];
};

export type CostVsBudgetForecastChartTimePoint = TimePoint & {
  forecast: number;
  budget: number;
};

export type BudgetYearly = {
  id: string;
  budgetByMonth: string[];
  yearlySum: string;
};

export const LinkQueryParameterName = {
  APPLICATION: "application",
  ENVIRONMENT: "environment",
  TYPE_SUBTYPE: "typeSubtype",
} as const;

type LinkQueryParameter = {
  name: string;
  value: string[];
};

export type LinkParameters = {
  queryParams: LinkQueryParameter[];
  otherCategoryIds: string[];
};

export type ChartDates = { date: string; displayDate: string };

export type ChartsQueryState = {
  page?: number;
  period?: Period;
  granularity?: Granularity;
  chartType?: string;
  rangeStartDate?: string;
  rangeEndDate?: string;
  costTab?: string;
  otherCosts?: boolean;
};

type EnvGroup = {
  id: string;
  name: string;
  value: string;
  types?: Maybe<Maybe<string>[]>;
};

export type TopGroupsCostChartProps = {
  date: string;
  value: string;
  totalCost?: string;
  groups?: Maybe<Maybe<EnvGroup>[]>;
};

export type DateRange = {
  from: string;
  to: string;
};

export type Anomaly = {
  anomaly: boolean;
  date: string;
  cost: number;
  forecastedCost?: number | null;
  upperCostEstimate?: number | null;
  lowerCostEstimate?: number | null;
  anomalyCost?: number | null;
  isAttached?: boolean;
};
